import { withPlugins, eventBus, domEvents } from '@spon/plugins';
import Headroom from 'headroom.js';
import gsap from 'gsap';
import toggle from '@/ui/toggle';

/**
 * SVG path morph values for the page-wipe transition.
 * Adapted from codrops sketch 021.
 *
 * step1: bottom-to-top (open cover / close reveal)
 * step2: top-to-bottom (open reveal / close cover)
 */
const paths = {
  step1: {
    unfilled: 'M 0 100 V 100 Q 50 100 100 100 V 100 z',
    inBetween: 'M 0 100 V 50 Q 50 0 100 50 V 100 z',
    filled: 'M 0 100 V 0 Q 50 0 100 0 V 100 z',
  },
  step2: {
    filled: 'M 0 0 V 100 Q 50 100 100 100 V 0 z',
    inBetween: 'M 0 0 V 50 Q 50 0 100 50 V 0 z',
    unfilled: 'M 0 0 V 0 Q 50 0 100 0 V 0 z',
  },
};

/**
 * SVG path morph values for the burger icon.
 * Inactive: three straight horizontal lines.
 * Active: middle fades out, top/bottom morph into crossing curves.
 */
const burgerLines = {
  inactive: {
    top: 'M 0 1.7 C 22 1.7 44 1.7 66 1.7',
    middle: 'M 0 9.5 C 22 9.5 44 9.5 66 9.5',
    bottom: 'M 0 17.3 C 22 17.3 44 17.3 66 17.3',
  },
  active: {
    top: 'M 0 16.7 C 29 16.7 38.5 1.7 66 1.7',
    bottom: 'M 0 1.7 C 29 1.7 38.5 16.7 66 16.7',
  },
};

function Header({ node, name, plugins: { addEvents } }) {
  if (!node) return;

  /*
   *		Elements
   */
  const burger = node.querySelector('.o-burger');
  const burgerTop = burger ? burger.querySelector('[data-burger-top]') : null;
  const burgerMiddle = burger
    ? burger.querySelector('[data-burger-middle]')
    : null;
  const burgerBottom = burger
    ? burger.querySelector('[data-burger-bottom]')
    : null;
  const svgPath = document.querySelector('.c-nav-overlay__path');
  const navPanel = document.querySelector('.c-nav-panel');
  const navTitles = navPanel
    ? Array.from(navPanel.querySelectorAll('[data-nav-title]'))
    : [];

  if (!svgPath || !navPanel) return;

  let isAnimating = false;

  /*
   *		Headroom
   */
  const headroom = new Headroom(node, {
    offset: node.clientHeight * 1.25 || 0,
    tolerance: { up: 5, down: 0 },
  });

  eventBus.on('menu:open', () => headroom.freeze());
  eventBus.on('menu:close', () => headroom.unfreeze());

  /*
   *		Body scroll lock
   */
  let windowTop = 0;
  const lock = {
    capture() {
      windowTop = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `${windowTop * -1}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
      headroom.freeze();
    },
    release() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      window.scroll(0, windowTop);
      setTimeout(() => headroom.unfreeze(), 1);
    },
  };

  /*
   *		Focus management
   */
  function setNavFocusable(enabled) {
    const value = enabled ? '0' : '-1';
    navTitles.forEach(link => {
      link.setAttribute('tabindex', value);
    });
    const logo = navPanel.querySelector('.c-nav-panel__logo');
    if (logo) logo.setAttribute('tabindex', value);
  }

  setNavFocusable(false);

  let removeFocusTrap = null;

  function trapFocus(container) {
    const focusableSelector =
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])';

    function handler(e) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        container.querySelectorAll(focusableSelector)
      );
      // Include the burger button in the focus cycle
      focusable.push(burger);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }

  /*
   *		Toggle (burger button)
   */
  const nav = toggle({
    button: burger,
    name,
    activeClass: 'is-active',
  });

  // Block clicks during animation (capture phase, before toggle handler)
  burger.addEventListener(
    'click',
    e => {
      if (isAnimating) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  /*
   *		Click outside to close
   */
  function clickWhenOpenHandle({ target }) {
    if (target.closest('#header') || target.closest('#primary-navigation'))
      return;
    nav.close();
  }

  /*
   *		Open: SVG wipe from bottom -> reveal nav -> stagger titles in
   */
  nav.on(`open:${name}`, () => {
    if (isAnimating) return;
    isAnimating = true;

    node.classList.add('nav-open');
    eventBus.emit('menu:open');
    lock.capture();

    gsap.set(navTitles, { yPercent: 100, opacity: 0 });

    gsap
      .timeline({
        onComplete() {
          isAnimating = false;
          setNavFocusable(true);
          removeFocusTrap = trapFocus(navPanel);
          if (navTitles[0]) navTitles[0].focus();
          document.addEventListener('click', clickWhenOpenHandle);
        },
      })
      // Burger: morph to crossed curves
      .to(
        burgerTop,
        {
          attr: { d: burgerLines.active.top },
          duration: 0.35,
          ease: 'power3.inOut',
        },
        0
      )
      .to(burgerMiddle, { opacity: 0, duration: 0.18, ease: 'power3.in' }, 0)
      .to(
        burgerBottom,
        {
          attr: { d: burgerLines.active.bottom },
          duration: 0.35,
          ease: 'power3.inOut',
        },
        0
      )
      // Phase 1: SVG covers screen from bottom (overlaps burger)
      .set(svgPath, { attr: { d: paths.step1.unfilled } }, 0)
      .to(
        svgPath,
        {
          duration: 0.56,
          ease: 'power4.in',
          attr: { d: paths.step1.inBetween },
        },
        0.1
      )
      .to(svgPath, {
        duration: 0.14,
        ease: 'power1',
        attr: { d: paths.step1.filled },
        onComplete() {
          navPanel.classList.add('is-open');
        },
      })
      // Phase 2: SVG reveals nav from top
      .set(svgPath, { attr: { d: paths.step2.filled } })
      .to(svgPath, {
        duration: 0.14,
        ease: 'power1',
        attr: { d: paths.step2.inBetween },
      })
      .to(svgPath, {
        duration: 0.7,
        ease: 'power4',
        attr: { d: paths.step2.unfilled },
      })
      // Phase 3: Stagger nav titles in
      .to(
        navTitles,
        {
          yPercent: 0,
          opacity: 1,
          duration: 0.42,
          stagger: 0.07,
          ease: 'power3.out',
        },
        '-=0.56'
      );
  });

  /*
   *		Close: stagger titles out -> SVG wipe from top -> reveal page
   */
  nav.on(`close:${name}`, () => {
    if (isAnimating) return;
    isAnimating = true;

    document.removeEventListener('click', clickWhenOpenHandle);

    if (removeFocusTrap) {
      removeFocusTrap();
      removeFocusTrap = null;
    }
    setNavFocusable(false);

    gsap
      .timeline({
        onComplete() {
          isAnimating = false;
          node.classList.remove('nav-open');
          eventBus.emit('menu:close');
          lock.release();
        },
      })
      // Burger: morph back to straight lines
      .to(
        burgerTop,
        {
          attr: { d: burgerLines.inactive.top },
          duration: 0.35,
          ease: 'power3.inOut',
        },
        0
      )
      .to(
        burgerMiddle,
        { opacity: 1, duration: 0.18, ease: 'power3.out' },
        0.18
      )
      .to(
        burgerBottom,
        {
          attr: { d: burgerLines.inactive.bottom },
          duration: 0.35,
          ease: 'power3.inOut',
        },
        0
      )
      // Phase 1: Stagger nav titles out
      .to(
        navTitles,
        {
          yPercent: -100,
          opacity: 0,
          duration: 0.21,
          stagger: 0.035,
          ease: 'power3.in',
        },
        0
      )
      // Phase 2: SVG covers screen from top
      .set(svgPath, { attr: { d: paths.step2.unfilled } })
      .to(svgPath, {
        duration: 0.56,
        ease: 'power4.in',
        attr: { d: paths.step2.inBetween },
      })
      .to(svgPath, {
        duration: 0.14,
        ease: 'power1',
        attr: { d: paths.step2.filled },
        onComplete() {
          navPanel.classList.remove('is-open');
        },
      })
      // Phase 3: SVG reveals page from bottom
      .set(svgPath, { attr: { d: paths.step1.filled } })
      .to(svgPath, {
        duration: 0.14,
        ease: 'power1',
        attr: { d: paths.step1.inBetween },
      })
      .to(svgPath, {
        duration: 0.7,
        ease: 'power4',
        attr: { d: paths.step1.unfilled },
      });
  });

  /*
   *		Graceful close for page navigation
   *
   *		The new page is already loaded behind the open menu.
   *		Plays the full close animation (identical to a normal close),
   *		revealing the new page underneath:
   *		  1. Titles out + burger morph
   *		  2. SVG wipe covers screen from top
   *		  3. Hide nav panel, release body lock, scroll to top
   *		  4. SVG reveals new page from bottom
   *		Resolves when the reveal is complete.
   */
  function closeForNavigation() {
    return new Promise(resolve => {
      isAnimating = true;

      document.removeEventListener('click', clickWhenOpenHandle);
      if (removeFocusTrap) {
        removeFocusTrap();
        removeFocusTrap = null;
      }
      setNavFocusable(false);

      gsap
        .timeline({
          onComplete() {
            isAnimating = false;
            node.classList.remove('nav-open');
            eventBus.emit('menu:close');
            resolve();
          },
        })
        // Burger: morph back to straight lines
        .to(
          burgerTop,
          {
            attr: { d: burgerLines.inactive.top },
            duration: 0.35,
            ease: 'power3.inOut',
          },
          0
        )
        .to(
          burgerMiddle,
          { opacity: 1, duration: 0.18, ease: 'power3.out' },
          0.18
        )
        .to(
          burgerBottom,
          {
            attr: { d: burgerLines.inactive.bottom },
            duration: 0.35,
            ease: 'power3.inOut',
          },
          0
        )
        // Phase 1: Stagger nav titles out
        .to(
          navTitles,
          {
            yPercent: -100,
            opacity: 0,
            duration: 0.21,
            stagger: 0.035,
            ease: 'power3.in',
          },
          0
        )
        // Phase 2: SVG covers screen from top
        .set(svgPath, { attr: { d: paths.step2.unfilled } })
        .to(svgPath, {
          duration: 0.56,
          ease: 'power4.in',
          attr: { d: paths.step2.inBetween },
        })
        .to(svgPath, {
          duration: 0.14,
          ease: 'power1',
          attr: { d: paths.step2.filled },
          onComplete() {
            // Screen is fully covered — safe to change state
            navPanel.classList.remove('is-open');
            gsap.set(navTitles, { clearProps: 'all' });

            burger.classList.remove('is-active');
            burger.setAttribute('aria-expanded', 'false');
            burger.title = 'Open primary menu';
            nav.isOpen = false;

            // Release body lock while hidden
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.overflow = '';
            document.body.style.width = '';

            headroom.unfreeze();

            // Hydrate new page behaviours while screen is
            // still covered so animations are already running
            // when the SVG reveal starts.
            eventBus.emit('smoothScroll:resume');
            eventBus.emit('smoothScroll:reset');
            eventBus.emit('page:entered');
            eventBus.emit('fadeIn:refresh');

            const ta = eventBus._textAnimation;
            if (ta) {
              ta.init();
              ta.animateIn({ delay: 0.2 });
            }
          },
        })
        // Phase 3: SVG reveals page from bottom
        .set(svgPath, { attr: { d: paths.step1.filled } })
        .to(svgPath, {
          duration: 0.14,
          ease: 'power1',
          attr: { d: paths.step1.inBetween },
        })
        .to(svgPath, {
          duration: 0.7,
          ease: 'power4',
          attr: { d: paths.step1.unfilled },
        });
    });
  }

  /*
   *		Expose nav state for PageTransition coordination
   */
  eventBus._headerNav = {
    isOpen: () => nav.isOpen,
    closeForNavigation,
  };

  /*
   *		Instant reset for Barba page transitions (fallback)
   *
   *		Skipped when the menu-to-page transition is active
   *		(closeForNavigation handles the animated close instead).
   */
  eventBus.on('page:exited', () => {
    if (!nav.isOpen) return;
    if (eventBus._menuTransitionActive) return;

    gsap.killTweensOf(svgPath);
    gsap.killTweensOf(navTitles);
    gsap.killTweensOf([burgerTop, burgerMiddle, burgerBottom]);

    navPanel.classList.remove('is-open');
    gsap.set(svgPath, { attr: { d: paths.step1.unfilled } });
    gsap.set(navTitles, { clearProps: 'all' });
    gsap.set(burgerTop, { attr: { d: burgerLines.inactive.top } });
    gsap.set(burgerMiddle, {
      attr: { d: burgerLines.inactive.middle },
      opacity: 1,
    });
    gsap.set(burgerBottom, { attr: { d: burgerLines.inactive.bottom } });

    node.classList.remove('nav-open');
    burger.classList.remove('is-active');

    if (removeFocusTrap) {
      removeFocusTrap();
      removeFocusTrap = null;
    }
    setNavFocusable(false);

    isAnimating = false;
    nav.isOpen = false;
    document.removeEventListener('click', clickWhenOpenHandle);

    lock.release();
  });

  /*
   *		Ensure header is visible after page transitions
   */
  eventBus.on('page:entered', () => {
    headroom.pin();
  });

  /*
   *		Keyboard
   */
  function onKeyDown(event) {
    if (event.key === 'Escape' && nav.isOpen && !isAnimating) {
      nav.close();
    }
  }

  /*
   *		Init
   */
  nav.init();
  headroom.init();

  addEvents(document.body, {
    keydown: onKeyDown,
  });
}

export default withPlugins(domEvents)(Header);
